(ns portal.advertiser-table.view
  (:require ["@mui/material/Button$default" :as Button]
            ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Tooltip$default" :as Tooltip]
            ["@mui/material/Typography$default" :as Typography]
            ["@mui/x-data-grid-premium" :refer [DataGridPremium gridSortedRowIdsSelector useGridApiContext]]
            ["moment/min/moment-with-locales$default" :as moment]
            ["react" :as react]
            ["src/svg/inform/plus.svg$default" :as Plus]
            [cljs-bean.core :refer [->clj]]
            [clojure.string :as str]
            [common.patterns.stats-daterange-select :refer [daterange-select get-initial-daterange recent-stats-daterange]]
            [common.queries :refer [use-queries use-query]]
            [common.resource.query.advertiser :as advertiser.q]
            [common.resource.query.agency :as agency.q]
            [common.util :refer [->locale-string format-timestamp left-join spread-obj]]
            [common.widgets.data-grid :as dg]
            [manage.ad-variant.creation.link.hooks :as link-hooks]
            [manage.adset-table.view :refer [derive-status format-status-with-tooltip status-tooltip]]
            [re-frame.core :as rf]
            [reitit.frontend.easy :as r.easy]
            [uix.core :as uix :refer [$ defhook defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defn get-unfiltered-rows [^js props]
  (gridSortedRowIdsSelector (.. props -apiRef)))

(def Toolbar
  (uix/as-react
   (fn [{:keys [daterange set-daterange is-advertiser-create-allowed agency-name]}]
     (let [api (-> (useGridApiContext) .-current)
           handle-export #(.. api (exportDataAsCsv %))]
       ($ dg/grid-toolbar
          ($ Stack
             {:direction "row"
              :sx #js {:flex 1}}
             ($ dg/filters-button
                {:filters [{:id "status"
                            :label "Status"
                            :field "status"
                            :filter-options (->> ["archived" "active" "inactive"]
                                                 (mapv #(hash-map :value % :label (str/capitalize %))))}]}))
          ($ Stack
             {:direction "row"
              :gap 1}
             ($ daterange-select
                {:value daterange
                 :on-change set-daterange})
             ($ Button
                {:onClick #(handle-export #js {:getRowsToExport get-unfiltered-rows
                                               :fileName agency-name})
                 :variant "outlined"
                 :data-testid "exportMetricsButton"}
                "Export metrics")
             (when is-advertiser-create-allowed
               ($ Button
                  {:onClick #(rf/dispatch [:modal/open {:id :advertiser/create}])
                   :variant "contained"
                   :startIcon ($ Plus)
                   :data-testid "createAdvertiserButton"}
                  "New advertiser"))))))))

(defui render-name-column [{:keys [name archived campaign-count isDemo]}]
  ($ Stack {:direction "row"
            :width "100%"
            :align-items "center"
            :justifyContent "space-between"}
     ($ Stack {:width "70%"}
        ($ Typography
           (cond-> {:noWrap true}
             archived (assoc :sx #js {:color "text.disabled"}))
           name)
        ($ Typography
           {:fontSize "sm"
            :sx #js {:color "grey.20"}}
           (str campaign-count
                " campaign" (when-not (= campaign-count 1) "s"))))
     (when isDemo
       ($ Chip {:label "Demo Account"}))))

(defui render-updated-at [{:keys [updated-at]}]
  ($ Tooltip
     {:placement "top"
      :title (format-timestamp updated-at)}
     ($ Typography
        {:component "span"}
        (.fromNow (moment (* 1000 updated-at))))))

(defui render-status [{:keys [status]}]
  (when status
    (let [tooltip (status-tooltip "advertiser" status)]
      ($ format-status-with-tooltip {:status status :tooltip tooltip}))))

(def ^:private columns
  #js [#js {:field "name"
            :headerName "Advertiser name"
            :type "string"
            :minWidth 200
            :flex 3
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:name row))
            :renderCell (fn [^js params]
                          ($ render-name-column (.-row params)))}
       #js {:field "opendata"
            :headerName "Open Data"
            :type "string"
            :minWidth 280
            :flex 1
            :headerAlign "right"
            :align "right"
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (->> (select-keys (:osWorkspaceInfo row) [:client :market :brand])
                                vals
                                (map :name)
                                (remove nil?)
                                (str/join "/")))
            :renderCell (fn [^js params]
                          ((fnil ->locale-string 0) (.-value params)))}
       #js {:field "impressions"
            :headerName "Impressions"
            :type "number"
            :minWidth 100
            :flex 1
            :headerAlign "right"
            :align "right"
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:impressions row))
            :renderCell (fn [^js params]
                          ((fnil ->locale-string 0) (.-value params)))}
       #js {:field "clicks"
            :headerName "Clicks"
            :type "number"
            :minWidth 100
            :flex 1
            :headerAlign "right"
            :align "right"
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:clicks row))
            :renderCell (fn [^js params]
                          ((fnil ->locale-string 0) (.-value params)))}
       #js {:field "ctr"
            :headerName "CTR"
            :type "number"
            :minWidth 100
            :flex 1
            :headerAlign "right"
            :align "right"
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:ctr row))
            :renderCell (fn [^js params]
                          (let [ctr (or (.-value params) 0)]
                            (-> (* 100 ctr)
                                (->locale-string {:minimumFractionDigits 3})
                                (str "%"))))}
       #js {:field "updated-at"
            :headerName "Last activity"
            :type "timestamp"
            :minWidth 120
            :flex 1
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:updatedAt row))
            :renderCell (fn [^js params]
                          ($ render-updated-at {:updated-at (.-value params)}))}
       #js {:field "status"
            :headerName "Status"
            :type "singleSelect"
            :minWidth 130
            :valueOptions #js ["archived" "active" "inactive"]
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:status row))
            :renderCell (fn [^js params]
                          ($ render-status {:status (.-value params)}))}])

(defn enrich-status
  [{:keys [archived impressions-recent createdAt] :as advertiser}]
  (assoc advertiser :status (derive-status archived impressions-recent createdAt)))

(defn get-recent-impressions [^js data]
  (->> data ->clj (mapv (fn [{:keys [advertiser-id impressions]}]
                          {:id advertiser-id
                           :impressions-recent impressions}))))

(defn combine-queries-results [^js results]
  (let [results (->clj results)
        [{advertisers :data} {stats :data} {recent-stats :data}] results]
    #js {:isPending (boolean (some :isPending results))
         :data (->> (cond-> advertisers
                      (seq stats) (left-join stats :id)
                      (seq recent-stats) (left-join recent-stats :id))
                    (mapv enrich-status))}))

(defhook use-advertisers-data [{:keys [agency-id daterange]}]
  (let [os-data (link-hooks/use-os-data)
        advertisers-query (if (:os-enabled os-data)
                            (advertiser.q/advertisers-enriched-query os-data)
                            (advertiser.q/advertisers-query))
        stats-query (advertiser.q/advertisers-stats-query {:agency-id agency-id :daterange daterange})
        recent-stats-query (spread-obj (advertiser.q/advertisers-stats-query {:agency-id agency-id :daterange recent-stats-daterange})
                                       #js {:select get-recent-impressions})]
    (use-queries #js {:queries #js [advertisers-query stats-query recent-stats-query]
                      :combine combine-queries-results})))

(defn- get-row-id [row]
  (:id row))

(defui advertiser-data-grid []
  (let [{:keys [agency-id] :as url-context} (use-subscribe [:url-context])
        [daterange set-daterange] (uix/use-state (get-initial-daterange))
        {:keys [data isPending]} (use-advertisers-data {:agency-id agency-id :daterange daterange})

        page-size 10

        initial-state (react/useMemo (fn []
                                       #js {:sorting #js {:sortModel #js [#js {:field "updated-at" :sort "desc"}]}
                                            :pagination #js {:paginationModel #js {:pageSize page-size}}
                                            :filter #js {:filterModel #js {:items #js [#js {:id "status"
                                                                                            :field "status"
                                                                                            :operator "isAnyOf"
                                                                                            :value #js ["active" "inactive"]}]}}})
                                     #js [page-size])

        {{agency-name :name} :data} (use-query (agency.q/agency-query {:agency-id agency-id}))
        is-advertiser-create-allowed (use-subscribe [:resource.user/allowed? "advertisers.create"])]
    ($ DataGridPremium
       {:columns columns
        :loading isPending

        :rows (to-array data)
        :getRowId get-row-id

        :disableRowSelectionOnClick true

        :pagination true
        :pageSizeOptions #js [page-size]

        :initialState initial-state

        :slots #js {:row dg/WithDynamicPropsRow
                    :cell dg/WithLinkCell
                    :toolbar Toolbar}
        :slotProps #js {:baseTextField dg/grid-toolbar-outlined-input
                        :row #js {:get-props-fn (fn [props]
                                                  {:data-testid (-> props :row :name)})}
                        :cell #js {:href-fn (fn [row]
                                              (r.easy/href :advertiser/configuration (assoc url-context
                                                                                            :advertiser-id (:id row))))}
                        :toolbar #js {:daterange daterange
                                      :set-daterange set-daterange
                                      :is-advertiser-create-allowed is-advertiser-create-allowed
                                      :agency-name agency-name}}

        :data-testid "table-advertiser"

        :noWrap true
        :sx #js {:maxHeight "70rem"}})))
